import { datadogRum } from '@datadog/browser-rum';
import { useQuery } from '@tanstack/react-query';

import { ensureImpactAdvocateLoader, waitForImpactEmbed } from './advocateLoader';

import useEventTracking from 'modules/common/components/AnalyticsTracking/useEventTracking';

const EMBED_LOAD_TIMEOUT_MS = 8000;

interface UseImpactEmbedAvailabilityOptions {
    enabled: boolean;
}

/**
 * Resolves whether the Impact `impact-embed` element ends up defined on this page. Users whose
 * network or content filter blocks the Advocate scripts never get it, and the answer does not
 * change within a session, so the result is cached for the app's lifetime.
 */
export default function useImpactEmbedAvailability({ enabled }: UseImpactEmbedAvailabilityOptions) {
    const { sendTrackingEvent } = useEventTracking();

    return useQuery<boolean>({
        queryKey: ['impact', 'useImpactEmbedAvailability'],
        queryFn: async () => {
            ensureImpactAdvocateLoader();
            const isAvailable = await waitForImpactEmbed(EMBED_LOAD_TIMEOUT_MS);

            if (!isAvailable) {
                const context = { timeoutMs: EMBED_LOAD_TIMEOUT_MS, location: window.location.pathname };
                datadogRum.addError(new Error('impact_widget_load_failed'), context);
                sendTrackingEvent('Referral Widget Load Failed', {
                    category: 'Referral Widget',
                    action: 'load_failed',
                    ...context,
                });
            }

            return isAvailable;
        },
        staleTime: Infinity,
        gcTime: Infinity,
        refetchOnWindowFocus: false,
        retry: false,
        enabled,
    });
}
